const formSectionHeaderSx = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.25),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "& .MuiCardHeader-action": {
    alignSelf: "center",
    marginTop: 0,
    marginRight: 0,
  },
  "& .MuiCardHeader-content": {
    minWidth: 0,
  },
});

const formSectionContentSx = (theme) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(2.5),
  "&:last-child": { paddingBottom: theme.spacing(2.5) },
});

const infoCardContentSx = (theme) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(3.5),
  },
  "&:last-child": { 
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      paddingBottom: theme.spacing(3.5),
    }
  },
});

export const cardRecipe = {
  intents: {
    schemeCard: ({ theme }) => ({
      backgroundColor: theme.palette.neutral.pageBg,
      borderRadius: "20px",
      border: `1px solid ${theme.palette.neutral.warmStroke}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
    }),
    infoCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: "16px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "0 2px 8px rgba(0,0,0,.06)",
      color: theme.palette.text.primary,
      "&:hover": {
        boxShadow: "0 4px 16px rgba(0,0,0,.1)",
      },
      "& .MuiCardContent-root": infoCardContentSx(theme),
      "& .MuiCardActionArea-root .MuiCardContent-root:last-child": {
        paddingBottom: theme.spacing(2),
        [theme.breakpoints.up("md")]: {
          paddingBottom: theme.spacing(3.5),
        }
      },
    }),
    assetCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: "16px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
    }),
    bankRegistryCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: "20px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
    }),
    agriProductCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: "12px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
      padding: "16px",
    }),
    formSectionCard: ({ theme }) => ({
      backgroundColor: theme.palette.neutral.surface,
      borderRadius: "16px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      overflow: "hidden",
      color: theme.palette.text.primary,
      "& .MuiCardHeader-root": formSectionHeaderSx(theme),
      "& .MuiCardContent-root": formSectionContentSx(theme),
    }),
    advisoryCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: "16px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      padding: theme.spacing(2),
    }),
    advisorySummaryCard: ({ theme }) => ({
      backgroundColor: theme.palette.neutral.springGoldSurface,
      borderRadius: "16px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2.5),
      padding: theme.spacing(2.5),
    }),
    compactCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: "12px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      padding: theme.spacing(1.5),
    }),
    aqiCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: "16px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(5),
      padding: theme.spacing(2.5),
      [theme.breakpoints.up("md")]: { flexDirection: "row" },
    }),
    helpCategoryCard: ({ theme }) => ({
      backgroundColor: theme.palette.categoryTabBg.green,
      borderRadius: "24px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
    }),
    callCentreCard: ({ theme }) => ({
      backgroundColor: theme.palette.categoryTabBg.yellow,
      borderRadius: "20px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
    }),
    directoryCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: "16px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
    }),
    containerCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      borderRadius: "12px",
      border: `1.5px solid ${theme.palette.divider}`,
      boxShadow: "none",
      padding: theme.spacing(1.5),
      color: theme.palette.text.primary,
    }),
    opacityCard: ({ theme }) => ({
      backgroundColor: "rgba(255, 255, 255, 0.15)",
      borderRadius: "8px",
      padding: theme.spacing(2),
      color: theme.palette.text.primary,
    }),
    mediaBannerCard: ({ theme }) => ({
      position: "relative",
      isolation: "isolate",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius * 4,
      boxShadow: "none",
      color: theme.palette.text.white,
      backgroundColor: theme.palette.primary.dark,
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        zIndex: 1,
        backgroundColor: theme.palette.overlay.scrimStrong,
      },
      "& > :not(img)": { position: "relative", zIndex: 2 },
    }),
    tintCard: ({ theme }) => ({
      backgroundColor: theme.palette.background.tint,
      borderRadius: theme.shape.borderRadius * 3,
      border: `1px solid ${theme.palette.neutral.searchStroke}`,
      boxShadow: "none",
      color: theme.palette.text.primary,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
      padding: theme.spacing(2),
    }),
    white: ({ theme }) => ({
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.neutral.surface
          : theme.palette.background.white,
      borderRadius: "8px",
      padding: theme.spacing(2),
      color: theme.palette.text.primary,
    }),
  },
};
